// Persona model — the single source of truth for the staff access tokens,
// shared by the web shell/session (apps/web) and the server (apps/server).
//
// Two distinct vocabularies live here (ADR-0013):
//
//   - FrontendKey  — the four web shells a user can land on / switch between
//                    (`desk`, `ops`, `comply`, `view`). One route tree each.
//   - PersonaToken — every token that can sit in `users.personas[]`. A superset
//                    of FrontendKey: it adds `admin` (the firm app-configuration
//                    / all-access role — "SuperAdmin" in product conversation)
//                    and `im-assistant` (a restricted Desk persona, mutually
//                    exclusive with `desk`).
//
// `Persona` (the active-persona ACL) is `FrontendKey | 'im-assistant'` — the
// active persona that governs every read and route admission. `admin` is never
// an *active* persona on the web shell (the SuperAdmin operates as `view`); it
// is a granted token that reveals the Administration section and auto-passes
// route admission.
//
// This module is pure (no I/O, no framework) so both apps consume the SAME
// constants and the SAME policy rather than each maintaining a parallel copy.

/** The four staff web shells, in canonical landing order. */
export const FRONTEND_KEYS = ['desk', 'ops', 'comply', 'view'] as const;
export type FrontendKey = (typeof FRONTEND_KEYS)[number];

/**
 * The active persona that governs reads + route admission. A superset of
 * `FrontendKey`: `im-assistant` is the first active persona that is not itself
 * a frontend (it drives the Desk shell via `personaToFrontend`).
 */
export type Persona = FrontendKey | 'im-assistant';

/**
 * Every token that may appear in `users.personas[]`. Adds the non-frontend
 * grants `admin` and `im-assistant` to the four frontend keys.
 */
export const PERSONA_TOKENS = [...FRONTEND_KEYS, 'admin', 'im-assistant'] as const;
export type PersonaToken = (typeof PERSONA_TOKENS)[number];

/** The non-frontend grants — tokens that are not themselves a web shell. */
export const ADMIN_TOKEN = 'admin' as const;
export const IM_ASSISTANT_TOKEN = 'im-assistant' as const;

/**
 * The Desk sidebar section keys an IM Assistant is BARRED from. Everything else
 * on the Desk sidebar is allowed. The bar is enforced at the data gate (the
 * Object Types behind these pages do not carry the `im-assistant` tag) and at
 * route admission — the sidebar omission is cosmetic reinforcement only.
 *
 * `suitability` — regulated-advice sign-off; `desk-vulnerability` — sensitive
 * client data. (The former Suitability Workbench page was retired.)
 * `contacts` — the three-tier client-model Contacts tier bars `im-assistant`
 * until acting-for scoping lands (ADR-0025 D0.3); without the sidebar bar the
 * page would 403 into a broken empty state.
 * (The Stage 3 TerceroClient umbrella tier carried the same D0.3 bar, but no
 * longer needs a sidebar entry here: the tier lost its own page, and the two
 * Client Book surfaces that absorbed it are already allowed.)
 * `suitability-records` — the SUR mandate-container tier (Stage 2) carries
 * the same D0.3 bar: `suitability-record` does not tag `im-assistant`, and
 * the comply-only assessment content sits behind the compliance wall (D5).
 */
export const IM_ASSISTANT_BARRED_DESK_SECTIONS = [
    'suitability',
    'desk-vulnerability',
    'contacts',
    'suitability-records',
    // `relationships` — the book-level relationship directory (SEP-176) reads
    // the `Relationship` tier, which carries the same D0.3 bar: the Object
    // Type does not tag `im-assistant`, so the page would 403 into a broken
    // empty state.
    'relationships',
] as const;

export function isFrontendKey(value: string): value is FrontendKey {
    return (FRONTEND_KEYS as readonly string[]).includes(value);
}

export function isPersonaToken(value: string): value is PersonaToken {
    return (PERSONA_TOKENS as readonly string[]).includes(value);
}

export function isActivePersona(value: string): value is Persona {
    return isFrontendKey(value) || value === IM_ASSISTANT_TOKEN;
}

/**
 * Map an active persona to the web shell it drives. Identity for the four
 * frontend keys; `im-assistant → desk` (the assistant works the Desk shell,
 * on a restricted page set).
 */
export function personaToFrontend(persona: Persona): FrontendKey {
    return persona === IM_ASSISTANT_TOKEN ? 'desk' : persona;
}

/**
 * The frontends a granted token set may land on / switch between, in canonical
 * order. `admin` is not a frontend, so it contributes nothing here (it reveals
 * Administration instead). `im-assistant` resolves to `desk` so an
 * `im-assistant`-only user is NOT locked out — they land on the Desk shell.
 *
 * De-duplicated: a user granted both `desk` and `im-assistant` (which
 * normalisation forbids, but a raw seed row might carry) yields `desk` once.
 */
export function grantedFrontends(grantedTokens: readonly string[]): FrontendKey[] {
    const out: FrontendKey[] = [];
    for (const key of FRONTEND_KEYS) {
        const granted =
            grantedTokens.includes(key) ||
            (key === 'desk' && grantedTokens.includes(IM_ASSISTANT_TOKEN));
        if (granted) out.push(key);
    }
    return out;
}

/** Whether the granted token set reveals the View → Administration section. */
export function grantsAdministration(grantedTokens: readonly string[]): boolean {
    return grantedTokens.includes(ADMIN_TOKEN);
}

/**
 * Decide whether a Desk sidebar section key is visible to the given active
 * persona. Every persona sees every Desk section EXCEPT an `im-assistant`,
 * who is barred from the suitability + vulnerability pages.
 */
export function isDeskSectionVisible(persona: Persona, sectionKey: string): boolean {
    if (persona !== IM_ASSISTANT_TOKEN) return true;
    return !(IM_ASSISTANT_BARRED_DESK_SECTIONS as readonly string[]).includes(sectionKey);
}

/**
 * The persona-access policy outcome consumed by the web shell.
 *
 * - `grantedFrontends` — shells the user may switch between (sidebar switcher,
 *   `/` landing). `im-assistant` resolves to `desk`.
 * - `frontend` — the shell the ACTIVE persona drives (`im-assistant → desk`).
 * - `showAdministration` — reveal the View → Administration section. Driven by
 *   the GRANTED `admin` token, not the active persona.
 */
export interface PersonaPolicy {
    readonly grantedFrontends: FrontendKey[];
    readonly frontend: FrontendKey;
    readonly showAdministration: boolean;
}

/**
 * Resolve the full persona-access policy for a session. Pure: granted tokens +
 * active persona in, policy out. Replaces the inline `grantedFrontends` filter
 * and the per-frontend section call the web shell used to scatter.
 */
export function resolvePersonaPolicy(
    grantedTokens: readonly string[],
    activePersona: Persona,
): PersonaPolicy {
    return {
        grantedFrontends: grantedFrontends(grantedTokens),
        frontend: personaToFrontend(activePersona),
        showAdministration: grantsAdministration(grantedTokens),
    };
}

// --- Role normalisation -----------------------------------------------------

/**
 * Outcome of normalising a requested persona-token set for add-user /
 * role-replace. `ok` carries the normalised token list (de-duplicated, in
 * canonical order); `error` carries a machine code + human message.
 *
 * The same rules run on BOTH the UI (the None / Desk / IM Assistant segmented
 * control + view checkboxes) and the server (independently, on every write) —
 * the server never trusts the client to have applied them.
 */
export type RoleNormalisation =
    | { ok: true; personas: PersonaToken[] }
    | { ok: false; code: RoleNormalisationError; message: string };

export type RoleNormalisationError =
    | 'empty'
    | 'unknown_token'
    | 'admin_not_assignable'
    | 'desk_im_exclusive';

/**
 * Validate + normalise a requested persona-token set (add-user / role-replace).
 *
 * Rules (ADR-0013):
 *   - at least one token (`empty`);
 *   - every token must be a known `PersonaToken` (`unknown_token`);
 *   - `admin` is never assignable through the UI path (`admin_not_assignable`)
 *     — minting a SuperAdmin stays a seed/db task, off the privilege-escalation
 *     path;
 *   - `desk` and `im-assistant` are mutually exclusive (`desk_im_exclusive`).
 *
 * Idempotent: feeding a normalised set back in returns the same set. Output is
 * de-duplicated and ordered per `PERSONA_TOKENS`.
 */
export function normalisePersonaTokens(requested: readonly string[]): RoleNormalisation {
    if (requested.length === 0) {
        return { ok: false, code: 'empty', message: 'Select at least one role.' };
    }

    const seen = new Set<PersonaToken>();
    for (const token of requested) {
        if (!isPersonaToken(token)) {
            return {
                ok: false,
                code: 'unknown_token',
                message: `Unknown role '${token}'.`,
            };
        }
        if (token === ADMIN_TOKEN) {
            return {
                ok: false,
                code: 'admin_not_assignable',
                message: 'The admin role is seed-managed and cannot be assigned here.',
            };
        }
        seen.add(token);
    }

    if (seen.has('desk') && seen.has(IM_ASSISTANT_TOKEN)) {
        return {
            ok: false,
            code: 'desk_im_exclusive',
            message: 'Desk and IM Assistant are mutually exclusive — choose one.',
        };
    }

    const personas = PERSONA_TOKENS.filter((t) => seen.has(t));
    return { ok: true, personas };
}
